/**
 * useCrmLossReasons - Hook para gerenciar Motivos de Perda do CRM
 *
 * Fornece listagem + mutations (upsert/delete) via RPCs SECURITY DEFINER:
 *   - crm_list_loss_reasons
 *   - crm_upsert_loss_reason
 *   - crm_delete_loss_reason
 *
 * RELEASE: 2026-09-03 v1.1.0 - Bloqueios do Workflow usam aviso modal global.
 * RELEASE: 2026-07-10 v1.0.0 - Versão inicial.
 */
import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';
import { useWorkflowGuardNotice } from '@/providers/WorkflowGuardNoticeProvider';

const logger = createLogger('CrmLossReasons');

// Estrutura de motivo retornada pela RPC
export interface LossReasonEntry {
  id: string;
  name: string;
  description: string | null;
  color: string | null;
  is_active: boolean;
  sort_order: number;
  usage_count: number;
  created_at: string;
  updated_at: string;
}

/**
 * Lista os motivos de perda do tenant.
 * @param includeInactive - Se true, inclui motivos desativados (tela de gestão).
 */
export function useCrmLossReasons(includeInactive = false) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  const query = useQuery({
    queryKey: ['crm-loss-reasons', tenantId, includeInactive],
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)('crm_list_loss_reasons', {
        p_include_inactive: includeInactive,
        p_tenant_hint: tenantId,
      });
      if (error) {
        logger.error('Erro ao listar motivos de perda', error);
        throw error;
      }
      return (data ?? []) as LossReasonEntry[];
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });

  const activeReasons = useMemo(
    () => (query.data ?? []).filter((r) => r.is_active),
    [query.data],
  );

  return {
    reasons: query.data ?? [],
    activeReasons,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export interface UpsertLossReasonInput {
  id?: string | null;
  name: string;
  description?: string | null;
  color?: string | null;
  sort_order?: number;
  is_active?: boolean;
}

/** Mutation: cria ou atualiza um motivo de perda */
export function useUpsertLossReason() {
  const qc = useQueryClient();
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  return useMutation({
    mutationFn: async (input: UpsertLossReasonInput) => {
      const { data, error } = await (supabase.rpc as any)('crm_upsert_loss_reason', {
        p_id: input.id ?? null,
        p_name: input.name,
        p_description: input.description ?? null,
        p_color: input.color ?? null,
        p_sort_order: input.sort_order ?? 0,
        p_is_active: input.is_active ?? true,
        p_tenant_hint: tenantId,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm-loss-reasons'] });
      toast.success('Motivo de perda salvo');
    },
    onError: (err: any) => {
      logger.error('Falha ao salvar motivo de perda', err);
      toast.error(err?.message || 'Erro ao salvar motivo');
    },
  });
}

/** Mutation: exclui (ou desativa) um motivo de perda */
export function useDeleteLossReason() {
  const qc = useQueryClient();
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await (supabase.rpc as any)('crm_delete_loss_reason', {
        p_id: id,
        p_tenant_hint: tenantId,
      });
      if (error) throw error;
      return data as { mode: 'hard_deleted' | 'soft_deleted'; deals_using: number };
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['crm-loss-reasons'] });
      if (result.mode === 'soft_deleted') {
        toast.success(
          `Motivo desativado — ${result.deals_using} negócio(s) ainda o utilizam.`,
        );
      } else {
        toast.success('Motivo excluído');
      }
    },
    onError: (err: any) => {
      logger.error('Falha ao excluir motivo', err);
      toast.error(err?.message || 'Erro ao excluir motivo');
    },
  });
}

/** Mutation dedicada: marca negócio como perdido com motivo obrigatório */
export function useMarkDealAsLostWithReason() {
  const qc = useQueryClient();
  const { currentTenant } = useAuth();
  const workflowNotice = useWorkflowGuardNotice();
  const tenantId = currentTenant?.id ?? null;

  return useMutation({
    mutationFn: async (input: {
      dealId: string;
      lossReasonId: string;
      targetPhaseId?: string | null;
      lossReasonNote?: string | null;
      /** v1.1.0: conversas ativas a encerrar junto com a perda */
      conversationIdsToClose?: string[] | null;
    }) => {
      const { data, error } = await (supabase.rpc as any)('mark_deal_as_lost', {
        p_deal_id: input.dealId,
        p_loss_reason_id: input.lossReasonId,
        p_target_phase_id: input.targetPhaseId ?? null,
        p_loss_reason_note: input.lossReasonNote ?? null,
        p_tenant_hint: tenantId,
        p_close_conversation_ids: input.conversationIdsToClose?.length
          ? input.conversationIdsToClose
          : null,
      });
      if (error) throw error;

      return data as {
        deal_id: string;
        phase_id: string | null;
        previous_phase_id: string | null;
        pipeline_id: string | null;
        loss_reason_id: string;
        closed_conversation_ids?: string[] | null;
      };
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['crm-deals'] });
      qc.invalidateQueries({ queryKey: ['crm-pipeline-stats'] });
      qc.invalidateQueries({ queryKey: ['crm-deal', data.deal_id] });
      qc.invalidateQueries({ queryKey: ['kanban-conversation-crm-data'] });
      if (data.pipeline_id) {
        if (data.previous_phase_id) {
          qc.invalidateQueries({
            queryKey: ['kanban-column', data.pipeline_id, data.previous_phase_id],
          });
        }
        if (data.phase_id) {
          qc.invalidateQueries({
            queryKey: ['kanban-column', data.pipeline_id, data.phase_id],
          });
        }
        qc.invalidateQueries({ queryKey: ['pipeline-phase-stats', data.pipeline_id] });
      }

      // v1.1.0: caches de chat afetados pelo encerramento das conversas
      const closedCount = data.closed_conversation_ids?.length ?? 0;
      if (closedCount > 0) {
        qc.invalidateQueries({ queryKey: ['conversations'] });
        qc.invalidateQueries({ queryKey: ['contact-conversations'] });
        qc.invalidateQueries({ queryKey: ['kanban-conversation-column'] });
        qc.invalidateQueries({ queryKey: ['deal-active-conversations', data.deal_id] });
      }

      toast.info(
        closedCount > 0
          ? `Negócio marcado como perdido — ${closedCount} conversa(s) encerrada(s)`
          : 'Negócio marcado como perdido',
      );
    },

    onError: (err: any) => {
      if (workflowNotice.showWorkflowGuardNotice(err)) return;
      const msg = err?.message || 'Erro ao marcar como perdido';
      toast.error(msg);
    },
  });
}
